/**
 * TD-B-P5.5.2 (2026-06-01) — UI auto-rules engine для tax_purpose.
 *
 * Per-organization CRUD-довідник правил автоматичного підбору
 * tax_purpose_id при створенні goods_sale items.
 *
 * Кожне правило має ОДНУ з 3 умов:
 *  - contractor_id (per-клієнт правило)
 *  - nomenclature_id (per-номенклатура)
 *  - nomenclature_group_id (per-група)
 *
 * Specificity-priority: nomenclature > group > contractor.
 * priority 1-10 (1 = найвищий).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { refs, type TaxPurposeRule } from '@/api/refs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

export default function TaxPurposeRulesPage() {
  const { availableOrgs } = usePermissionsByOrg('refs.tax_purpose_rules.manage');
  const [selectedOrg, setSelectedOrg] = useState<string>('');

  useEffect(() => {
    const first = availableOrgs[0];
    if (!selectedOrg && first) {
      setSelectedOrg(first.id);
    }
  }, [availableOrgs, selectedOrg]);

  const rulesQuery = useQuery({
    queryKey: ['tax-purpose-rules', selectedOrg],
    queryFn: () => refs.taxPurposeRules.list(selectedOrg),
    enabled: !!selectedOrg,
  });
  const taxPurposesQuery = useQuery({
    queryKey: ['tax-purposes'],
    queryFn: refs.taxPurposes,
  });

  const qc = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: (id: string) => refs.taxPurposeRules.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tax-purpose-rules', selectedOrg] });
    },
  });

  const purposeMap = useMemo(() => {
    const m = new Map<string, string>();
    taxPurposesQuery.data?.forEach((tp) => m.set(tp.id, tp.name_uk));
    return m;
  }, [taxPurposesQuery.data]);

  const conditionOf = (rule: TaxPurposeRule): string => {
    if (rule.nomenclature_id) return `Номенклатура: ${rule.nomenclature_id.slice(0, 8)}`;
    if (rule.nomenclature_group_id) return `Група: ${rule.nomenclature_group_id.slice(0, 8)}`;
    if (rule.contractor_id) return `Контрагент: ${rule.contractor_id.slice(0, 8)}`;
    return '—';
  };

  const cols = useMemo<ColumnDef<TaxPurposeRule>[]>(() => [
    {
      key: 'priority', header: 'Пріоритет', width: 100, align: 'center',
      sortValue: (r) => r.priority, cell: (r) => r.priority,
    },
    {
      // Без width — гнучка колонка.
      key: 'condition', header: 'Умова',
      sortValue: (r) => conditionOf(r).toLowerCase(), cell: (r) => conditionOf(r),
    },
    {
      key: 'tax_purpose_id', header: 'Призн. ПДВ', width: 220,
      sortValue: (r) => (purposeMap.get(r.tax_purpose_id) ?? '').toLowerCase(),
      cell: (r) => purposeMap.get(r.tax_purpose_id) ?? '—',
    },
    {
      key: 'is_active', header: 'Активне', width: 90, align: 'center',
      sortValue: (r) => (r.is_active ? 1 : 0),
      cell: (r) => r.is_active
        ? <span title="Активне" style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
        : <span title="Не активне" style={{ color: '#94a3b8' }}>✗</span>,
    },
    {
      key: 'comment', header: 'Коментар', width: 220,
      sortValue: (r) => (r.comment ?? '').toLowerCase(), cell: (r) => r.comment ?? '',
    },
    {
      key: 'actions', header: '', width: 120, align: 'center',
      cell: (r) => (
        <button
          onClick={() => {
            if (confirm('Видалити правило?')) {
              deleteMutation.mutate(r.id);
            }
          }}
          className="danger"
        >
          Видалити
        </button>
      ),
    },
  ], [purposeMap, deleteMutation]);

  if (availableOrgs.length === 0) {
    return <div className="page">У вас немає прав на жодну організацію.</div>;
  }

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Авто-правила для пропорційного ПДВ</h2>
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {availableOrgs.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>

      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', maxWidth: 800 }}>
        Правила автоматично заповнюють поле «Призн. ПДВ» у рядках реалізації,
        якщо бухгалтер не вказав явно. Specificity: <strong>номенклатура</strong> →
        <strong> група</strong> → <strong>контрагент</strong>. У межах одного
        рівня — найменший <em>priority</em> (1 = найвищий).
      </p>

      <DataTable<TaxPurposeRule>
        rows={rulesQuery.data ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={rulesQuery.isLoading}
        emptyText="Правил ще не створено. Додайте перше правило через API (UI editor — окрема порція)."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="priority"
      />

      <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '1rem' }}>
        💡 Створення нових правил через UI — окрема порція (TD-B-P5.5.2 backend готовий).
        Поки що використовуйте API <code>POST /refs/tax-purpose-rules</code>.
      </p>
    </div>
  );
}
